import React from "react";
import { useSelector } from "react-redux";
import { Container, Skeleton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import styles from "./deliveryGuidesTableStyles.js";
import NoData from '../../../components/SharedComponents/NoData';
import DeliveryGuidesTableActions from "../DeliveryGuidesTableActions/index.js";
import { usePermissions } from "../../../hooks/permissions/usePermissions.js";

const DeliveryGuidesTable = ({
  guides = [],
  isLoading,
  search,
  fetchGuides,
  handleDialogOpen,
}) => {
  const { extraInformation } = useSelector((state) => state.user);
  const isSuperAdmin = extraInformation?.id_role === 5;

  const { hasAllPermissions } = usePermissions();

  const columns = [
    {
      field: "tracking_code",
      headerName: "CÓDIGO",
      width: 120,
      resizable: false,
      sortable: false,
      renderHeader: (params) => <strong>CÓDIGO</strong>,
    },
    {
      field: "creation_date",
      headerName: "FECHA DE CREACIÓN",
      type: "string",
      width: 200,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>FECHA DE CREACIÓN</strong>,
    },
    {
      field: "address",
      headerName: "DIRECCIÓN",
      type: "string",
      minWidth: 70,
      flex: 1,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>DIRECCIÓN</strong>,
    },
    ...(isSuperAdmin
      ? [
          {
            field: "region",
            headerName: "REGIÓN",
            type: "string",
            width: 110,
            resizable: false,
            sortable: false,
            headerAlign: "left",
            align: "left",
            renderHeader: (params) => <strong>REGIÓN</strong>,
          },
        ]
      : []),
    {
      field: "postal_code",
      headerName: "CP",
      type: "string",
      width: 70,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>CP</strong>,
    },
    {
      field: "status",
      headerName: "ESTADO",
      type: "string",
      width: 100,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>ESTADO</strong>,
    },
    /* {
      field: "actions",
      headerName: "ACCIONES",
      type: "string",
      width: 120,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>ACCIONES</strong>,
      renderCell: (params) => <DeliveryGuidesTableActions fetchGuides={fetchGuides} guide={params.row} handleDialogOpen={handleDialogOpen} />,
    }, */
  ];

  if (hasAllPermissions(["dgu_del", 'dgu_vw'])) {
    columns.push({
      field: "actions",
      headerName: "ACCIONES",
      type: "string",
      width: 120,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>ACCIONES</strong>,
      renderCell: (params) => (
        <DeliveryGuidesTableActions
          fetchGuides={fetchGuides}
          guide={params.row}
          handleDialogOpen={handleDialogOpen}
        />
      ),
    });
  }

  const filteredRows = guides.filter((guide) => {
    const transformedUser = {
      ...guide,
    };

    return Object.values(transformedUser).some((value) =>
      value.toString().toLowerCase().includes(search.toLowerCase())
    );
  });

  const rows = filteredRows?.map((guide, index) => ({
    id: guide.id_guide,
    id_batch: guide.id_batch,
    tracking_code: guide.tracking_code,
    creation_date: guide.creation_date,
    address: `
      ${guide?.street_name}
      ${guide?.colony}
      ${guide?.ext_number ? `ext. #${guide?.ext_number}` : ''} 
      ${guide?.int_number ? `int. ${guide?.int_number}` : ''}`,
    region: guide.zone_name || "Sin región",
    zone: guide.zone,
    area: guide.area,
    postal_code: guide.postal_code,
    status: guide.actual_status,
  }));

  return (
    <Container sx={styles.mainContainer}>
      {isLoading ? (
        <Container sx={styles.skeletonMainContainer}>
          <Container sx={styles.skeletonSecondaryContainer}>
            <Skeleton variant="rounded" width={'5%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'20%'} height={40} />
            <Skeleton variant="rounded" width={'25%'} height={40} />
            {isSuperAdmin && (
              <Skeleton variant="rounded" width={'10%'} height={40} />
            )}
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'15%'} height={40} />
          </Container>
          <Container sx={styles.skeletonSecondaryContainer}>
            <Skeleton variant="rounded" width={'5%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'20%'} height={300} />
            <Skeleton variant="rounded" width={'25%'} height={300} />
            {isSuperAdmin && (
              <Skeleton variant="rounded" width={'10%'} height={300} />
            )}
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'15%'} height={300} />
          </Container>
        </Container>
      ) : (
        <DataGrid
          autoHeight
          disableColumnFilter
          rows={rows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 20, 50, 100]}
          disableColumnMenu={true}
          localeText={{ 
            noRowsLabel: <NoData title="No se encontraron guías" />
          }}
          getRowHeight={() => 'auto'} 
          sx={{
            '& .MuiDataGrid-row.unselectableRow': styles.unselectableRow,
            '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
            '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
            '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '22px' },
          }}
        />
      )}
    </Container>
  );
};

export default DeliveryGuidesTable;
